import { Component, Input, OnInit } from '@angular/core';
import { ActionSheetController, ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { DocumentScanner, ScannerMode } from '@capgo/capacitor-document-scanner';
import { jsPDF } from 'jspdf';
import { JavapiService } from 'src/app/services/javapi.service';
import { MetodosgenService } from 'src/app/services/metodosgen.service';
import { Metodosui } from 'src/app/services/metodosui';

@Component({
    selector: 'app-ars-management',
    templateUrl: './ars-management.component.html',
    styleUrls: ['./ars-management.component.scss'],
    standalone: false
})
export class ArsManagementComponent implements OnInit {

    @Input() trabajo: any;
    @Input() clienteNombre: string = '';
    @Input() cotizacionId: number | null = null;

    arsInfo: any = {
        firmado: 'N',
        fecha_firma: null,
        archivo_pdf: null,
        if_pdf: null
    };

    isSigned: boolean = false;
    inftGenerado: boolean = false;
    inftFileName: string = '';
    fechaFirma: string = ''; // Format: YYYY-MM-DD
    isLoading: boolean = false;
    isSaving: boolean = false;
    usuarioActual: string = '';

    selectedFile: File | null = null;
    fileName: string = '';
    permissions: any = {};

    /** PDF del Informe Fotográfico generado en esta sesión */
    informeGenerado: { url: string; cotizacionNum: string } | null = null;

    hasCotizacionVinculada: boolean = false;

    constructor(
        private modalCtrl: ModalController,
        private actionSheetCtrl: ActionSheetController,
        private javapiService: JavapiService,
        private metodosGenService: MetodosgenService,
        private metodosUi: Metodosui
    ) { }

    ngOnInit() {
        this.permissions = this.metodosGenService.getPermissions();
        const userJson = localStorage.getItem('user');
        if (userJson) {
            this.usuarioActual = JSON.parse(userJson).l_usuario || 'system';
        }
        this.fechaFirma = '';
        this.loadArsInfo();
        this.checkCotizacionVinculada();
    }

    async checkCotizacionVinculada() {
        if (this.cotizacionId && Number(this.cotizacionId) > 0) {
            this.hasCotizacionVinculada = true;
            return;
        }
        try {
            const payload = this.metodosGenService.setModeloGeneral(
                'cotizaciones', 'cotizacionesT',
                String(this.trabajo.id),
                '', '', '', '', '', '', '', '', '', '', '', '', '', ''
            );
            const res: any = await firstValueFrom(this.javapiService.getCommonModel(payload));
            if (Array.isArray(res) && res.length > 0) {
                this.hasCotizacionVinculada = true;
                this.cotizacionId = Number(res[0].dt1) || null;
            } else {
                this.hasCotizacionVinculada = false;
            }
        } catch (e) {
            console.error('Error checking cotizacion vinculada:', e);
            this.hasCotizacionVinculada = false;
        }
    }

    presentNoCotizacionToast() {
        this.metodosUi.showToast('El trabajo debe tener una cotización vinculada para poder subir el reporte ARS.', 'warning');
    }

    async loadArsInfo() {
        this.isLoading = true;
        try {
            const payload = this.metodosGenService.setModeloGeneral(
                "genModel", "getArsInfo",
                String(this.trabajo.id),
                '', '', '', '', '', '', '', '', '', '', '', '', '', ''
            );

            const res: any = await firstValueFrom(this.javapiService.getCommonModel(payload));
            if (res && res.length > 0 && !(res[0] as any).Error) {
                this.arsInfo = {
                    firmado: res[0].dt1,
                    fecha_firma: res[0].dt2,
                    archivo_pdf: res[0].dt3,
                    if_pdf: res[0].dt4,
                    editor_json: res[0].dt5,
                    if_editor_json: res[0].dt6
                };
                this.isSigned = this.arsInfo.firmado === 'Y';

                // Si existe un PDF de informe fotográfico, cargar los datos para ver/editar
                if (this.arsInfo.if_pdf) {
                    let cleanName = this.arsInfo.if_pdf.replace('.pdf', '');
                    if (cleanName.includes('_')) {
                        const parts = cleanName.split('_');
                        cleanName = parts[parts.length - 1];
                    }
                    const url = this.javapiService.getFileUrl('INF', this.trabajo.id, false);
                    this.informeGenerado = {
                        url: url,
                        cotizacionNum: cleanName
                    };
                } else {
                    this.informeGenerado = null;
                }

                // Sincronizar existencia de INFT
                if (this.arsInfo.archivo_pdf && this.arsInfo.if_pdf) {
                    try {
                        const existsRes: any = await firstValueFrom(
                            this.javapiService.genericPost(`api/pg/trabajos/informe-final/exists/${this.trabajo.id}`, {})
                        );
                        this.inftGenerado = !!(existsRes && existsRes.exists);
                        this.inftFileName = (existsRes && existsRes.fileName) ? existsRes.fileName : `INFT_${this.trabajo.id}.pdf`;
                    } catch (err) {
                        console.error('Error checking final report existence', err);
                        this.inftGenerado = false;
                        this.inftFileName = '';
                    }
                } else {
                    this.inftGenerado = false;
                    this.inftFileName = '';
                }

                // Set initial date for picker
                if (this.arsInfo.fecha_firma) {
                    // Convert DD/MM/YYYY to YYYY-MM-DD for ion-datetime
                    const parts = this.arsInfo.fecha_firma.split('/');
                    if (parts.length === 3) {
                        this.fechaFirma = `${parts[2]}-${parts[1]}-${parts[0]}`;
                    }
                } else {
                    this.fechaFirma = '';
                }
            } else {
                this.fechaFirma = '';
            }
        } catch (e) {
            console.error('Error loading ARS info', e);
        } finally {
            this.isLoading = false;
        }
    }

    dismiss() {
        this.modalCtrl.dismiss();
    }

    async saveStatus() {
        if (!this.fechaFirma) {
            this.metodosUi.showToast('Selecciona una fecha de firma antes de guardar', 'warning');
            return;
        }
        await this.updateStatus('Y');
    }

    async removeFecha() {
        const confirm = await this.metodosUi.showConfirmation(
            'Quitar Fecha de Firma',
            '¿Deseas remover la fecha de firma del reporte? El estado de firma quedará como no firmado si no hay archivo PDF.'
        );
        if (!confirm) return;

        this.fechaFirma = '';
        const newState = this.arsInfo.archivo_pdf ? 'Y' : 'N';
        await this.updateStatus(newState);
    }

    private async updateStatus(state: 'Y' | 'N', showToast: boolean = true) {
        this.isSaving = true;
        try {
            const payload = this.metodosGenService.setModeloGeneral(
                "genUpdate", "updateArsStatus",
                String(this.trabajo.id),
                state,
                this.fechaFirma,
                this.usuarioActual,
                '', '', '', '', '', '', '', '', '', '', ''
            );

            const res = await firstValueFrom(this.javapiService.getCommonModel(payload));
            if (res) {
                if (showToast) {
                    this.metodosUi.showToast(
                        state === 'Y' ? (this.fechaFirma ? 'Fecha de firma guardada' : 'Reporte marcado como firmado') : 'Fecha de firma removida',
                        'success'
                    );
                }
                this.loadArsInfo();
            }
        } catch (e) {
            console.error('Error updating ARS status', e);
            this.metodosUi.showToast('Error al actualizar estado', 'danger');
        } finally {
            this.isSaving = false;
        }
    }

    onFileSelected(event: any) {
        const file = event.target.files[0];
        if (file) {
            if (file.type !== 'application/pdf') {
                this.metodosUi.showToast('Solo se permiten archivos PDF', 'warning');
                return;
            }
            this.selectedFile = file;
            this.fileName = file.name;

            // Si ya existe un reporte registrado, se sube automáticamente para reemplazarlo
            if (this.arsInfo && this.arsInfo.archivo_pdf) {
                this.uploadPdf();
            }
        }
    }

    async uploadPdf() {
        if (!this.selectedFile) return;

        if (!this.hasCotizacionVinculada) {
            this.metodosUi.showToast('El trabajo debe tener una cotización vinculada para poder subir el reporte ARS.', 'warning');
            return;
        }

        this.isSaving = true;
        try {
            // Guardar con el nombre original del archivo
            const targetFileName = this.selectedFile.name;
            const reader = new FileReader();

            const fileData: any = await new Promise((resolve, reject) => {
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(this.selectedFile!);
            });

            const base64: string = fileData.split(',')[1];
            const uploadRes: any = await firstValueFrom(this.javapiService.uploadArsFile(this.trabajo.id, targetFileName, base64));
            
            const savedFileName = (uploadRes && uploadRes.filename) ? uploadRes.filename : targetFileName;

            const payload = this.metodosGenService.setModeloGeneral(
                "genUpdate", "uploadArsPdf",
                String(this.trabajo.id),
                savedFileName,
                this.usuarioActual,
                '', '', '', '', '', '', '', '', '', '', '', ''
            );

            await firstValueFrom(this.javapiService.getCommonModel(payload));

            // Al subir el PDF, marcar automáticamente como firmado ('Y') manteniendo la fecha de firma si ya existe
            if (this.arsInfo.firmado !== 'Y') {
                await this.updateStatus('Y', false);
            }

            this.metodosUi.showToast('Archivo ARS subido correctamente', 'success');
            this.selectedFile = null;
            this.fileName = '';
            this.loadArsInfo();

        } catch (e) {
            console.error('Error uploading ARS PDF', e);
            this.metodosUi.showToast('Error al subir el archivo', 'danger');
        } finally {
            this.isSaving = false;
        }
    }

    async viewPdf() {
        if (this.arsInfo && this.arsInfo.archivo_pdf) {
            const url = this.javapiService.getFileUrl('ARS', this.trabajo.id, false);
            await Browser.open({ url });
        }
    }

    async deleteFile() {
        const resConfirm = await this.metodosUi.showConfirmation(
            'Confirmar Eliminación',
            '¿Estás seguro de que deseas eliminar este archivo? Se borrará del servidor y de la base de datos.'
        );

        if (resConfirm) {
            this.isSaving = true;
            try {
                const res: any = await firstValueFrom(
                    this.javapiService.genericPost(`api/pg/trabajos/file/delete/ARS/${this.trabajo.id}`, {})
                );

                if (res && res.status === 1) {
                    this.arsInfo.archivo_pdf = null;
                    // Al eliminar el PDF, si no hay fecha de firma, se marca como no firmado ('N') sin alterar la fecha
                    const newState = this.fechaFirma ? 'Y' : 'N';
                    await this.updateStatus(newState, false);
                    this.metodosUi.showToast('Archivo eliminado correctamente', 'success');
                    this.loadArsInfo(); // Refresh state
                } else {
                    this.metodosUi.showToast('Error al eliminar: ' + (res.message || ''), 'danger');
                }
            } catch (error) {
                console.error("Error deleting file", error);
                this.metodosUi.showToast('Error de conexión al intentar eliminar', 'danger');
            } finally {
                this.isSaving = false;
            }
        }
    }

    async openInformeFotografico() {
        try {
            const payload = this.metodosGenService.setModeloGeneral(
                "genModel", "trabajosGaleria", String(this.trabajo.id), "", "", "", "", "", "", "", "", "", "", "", "", "", ""
            );
            const res: any = await firstValueFrom(this.javapiService.getCommonModel(payload));

            const hasImages = Array.isArray(res) && res.some(d => d.dt3 && d.dt3.length > 5);
            if (!hasImages) {
                this.metodosUi.showToast('Se requiere al menos una imagen en la galería para crear el informe fotográfico.', 'warning');
                return;
            }
        } catch (error) {
            console.error("Error checking gallery for photo report", error);
        }

        const { InformeFotograficoComponent } = await import('../informe-fotografico/informe-fotografico.component');
        const modal = await this.modalCtrl.create({
            component: InformeFotograficoComponent,
            componentProps: {
                trabajo: this.trabajo,
                clienteNombre: this.clienteNombre,
                cotizacionId: this.cotizacionId
            }
        });
        await modal.present();

        // Capturar resultado: si se generó un informe exitosamente
        const { data } = await modal.onDidDismiss();
        if (data?.success) {
            this.informeGenerado = { url: data.pdfUrl, cotizacionNum: data.cotizacionNum };
            this.loadArsInfo(); // Recargar de DB para sincronizar la columna if_pdf
        }
    }

    /** Abre el PDF del informe generado en el navegador */
    async viewInformePdf() {
        if (!this.informeGenerado) return;
        await Browser.open({ url: this.informeGenerado.url });
    }

    /** Re-abre el modal de informe para editar (reutiliza el mismo modal) */
    async editarInforme() {
        await this.openInformeFotografico();
    }

    /** Despliega el ActionSheet premium para el informe fotográfico */
    async presentInformeActionSheet() {
        const actionSheet = await this.actionSheetCtrl.create({
            header: 'Informe Fotográfico',
            cssClass: 'custom-action-sheet',
            buttons: [
                {
                    text: 'Ver PDF',
                    icon: 'eye-outline',
                    handler: () => {
                        this.viewInformePdf();
                    }
                },
                {
                    text: 'Editar Informe',
                    icon: 'create-outline',
                    handler: () => {
                        actionSheet.dismiss();
                        setTimeout(() => {
                            this.editarInforme();
                        }, 300);
                        return false;
                    }
                },
                {
                    text: 'Eliminar Informe',
                    icon: 'trash-outline',
                    role: 'destructive',
                    handler: () => {
                        actionSheet.dismiss();
                        setTimeout(() => {
                            this.deleteInformePdf();
                        }, 300);
                        return false;
                    }
                },
                {
                    text: 'Cancelar',
                    icon: 'close-outline',
                    role: 'cancel'
                }
            ]
        });
        await actionSheet.present();
    }

    /** Despliega el ActionSheet premium para el reporte ARS */
    async presentArsActionSheet() {
        let actionSheet: any;

        const buttons: any[] = [
            {
                text: 'Ver Reporte',
                icon: 'eye-outline',
                handler: () => {
                    this.viewPdf();
                }
            },
            {
                text: 'Editar / Reemplazar Reporte',
                icon: 'cloud-upload-outline',
                handler: () => {
                    actionSheet.dismiss();
                    setTimeout(() => {
                        this.presentUploadOptionSheet();
                    }, 300);
                    return false;
                }
            },
            {
                text: 'Eliminar Reporte',
                icon: 'trash-outline',
                role: 'destructive',
                handler: () => {
                    actionSheet.dismiss();
                    setTimeout(() => {
                        this.deleteFile();
                    }, 300);
                    return false;
                }
            },
            {
                text: 'Cancelar',
                icon: 'close-outline',
                role: 'cancel'
            }
        ];

        actionSheet = await this.actionSheetCtrl.create({
            header: 'Reporte ARS',
            cssClass: 'custom-action-sheet',
            buttons: buttons
        });
        await actionSheet.present();
    }

    /** Dispara la selección de un nuevo archivo ARS */
    editarArsPdf() {
        const inputEl = document.getElementById('ars-file-input');
        if (inputEl) {
            inputEl.click();
        }
    }

    /** ActionSheet para elegir entre Escanear o Subir archivo PDF */
    async presentUploadOptionSheet() {
        if (this.permissions?.trabajos?.rl_gestionar_ars !== true) return;

        if (!this.hasCotizacionVinculada) {
            this.metodosUi.showToast('El trabajo debe tener una cotización vinculada para poder subir el reporte ARS.', 'warning');
            return;
        }

        const actionSheet = await this.actionSheetCtrl.create({
            header: '¿Cómo deseas subir el reporte?',
            cssClass: 'custom-action-sheet',
            buttons: [
                {
                    text: 'Escanear Documento',
                    icon: 'scan-outline',
                    handler: () => {
                        this.scanDocument();
                    }
                },
                {
                    text: 'Subir archivo PDF',
                    icon: 'document-attach-outline',
                    handler: () => {
                        actionSheet.dismiss();
                        setTimeout(() => {
                            const inputEl = document.getElementById('ars-file-input');
                            if (inputEl) inputEl.click();
                        }, 300);
                        return false;
                    }
                },
                {
                    text: 'Cancelar',
                    icon: 'close-outline',
                    role: 'cancel'
                }
            ]
        });
        await actionSheet.present();
    }

    /** Escanea documento con la cámara nativa (edge detection + crop) y abre el editor */
    async scanDocument() {
        try {
            // Verificar plataforma nativa
            if (!Capacitor.isNativePlatform()) {
                this.metodosUi.showToast('El escáner solo está disponible en dispositivo móvil', 'warning');
                return;
            }

            const result = await DocumentScanner.scanDocument({
                letUserAdjustCrop: true,
                maxNumDocuments: 5,
                scannerMode: ScannerMode.BaseWithFilter
            });

            if (!result.scannedImages || result.scannedImages.length === 0) {
                this.metodosUi.showToast('No se escaneó ningún documento', 'warning');
                return;
            }

            this.isSaving = true;
            this.metodosUi.showToast('Procesando imágenes escaneadas...', 'primary');

            // Leer todas las imágenes escaneadas como base64
            const base64Images: string[] = [];
            for (let i = 0; i < result.scannedImages.length; i++) {
                const imgUri = result.scannedImages[i];
                let base64Data: string;
                try {
                    let filePath = imgUri;
                    if (filePath.startsWith('file://')) {
                        filePath = filePath.replace('file://', '');
                    }

                    const fileResult = await Filesystem.readFile({
                        path: filePath
                    });
                    base64Data = typeof fileResult.data === 'string'
                        ? fileResult.data
                        : await this.blobToBase64(fileResult.data as Blob);
                } catch (readErr) {
                    console.error('Error reading scanned image, trying fetch fallback:', readErr);
                    const webPath = Capacitor.convertFileSrc(imgUri);
                    const response = await fetch(webPath);
                    const blob = await response.blob();
                    base64Data = await this.blobToBase64(blob);
                }
                base64Images.push(base64Data);
            }

            // Abrir el editor de documentos para que el usuario anote/rote/recorte antes de guardar
            await this.abrirArsEditor(base64Images, null);

        } catch (error: any) {
            console.error('Error scanning document:', error);
            if (error?.message?.includes('cancel') || error?.code === 'DOCUMENT_SCANNER_ERROR_CANCELED') {
                return;
            }
            this.metodosUi.showToast('Error al escanear el documento', 'danger');
        } finally {
            this.isSaving = false;
        }
    }

    async abrirArsEditor(rawImages: string[], initialState: any) {
        const { ArsEditorComponent } = await import('../ars-editor/ars-editor.component');
        const modal = await this.modalCtrl.create({
            component: ArsEditorComponent,
            componentProps: {
                rawImages: rawImages,
                initialEditorState: initialState,
                documentType: 'ARS',
                trabajoId: this.trabajo.id,
                cotizacionId: this.cotizacionId
            }
        });
        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data?.success) {
            this.loadArsInfo(); // Recargar la información del ARS
        }
    }

    /** Convierte un Blob a base64 string (sin el prefijo data:) */
    private blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                resolve(result.split(',')[1]);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }

    /** Elimina físicamente el PDF del servidor y actualiza la base de datos */
    async deleteInformePdf() {
        const resConfirm = await this.metodosUi.showConfirmation(
            'Confirmar Eliminación',
            '¿Estás seguro de que deseas eliminar este Informe Fotográfico? Se borrará del servidor y de la base de datos.'
        );

        if (resConfirm) {
            this.isSaving = true;
            try {
                const res: any = await firstValueFrom(
                    this.javapiService.genericPost(`api/pg/trabajos/file/delete/INF/${this.trabajo.id}`, {})
                );

                if (res && res.status === 1) {
                    this.metodosUi.showToast('Informe Fotográfico eliminado correctamente', 'success');
                    this.informeGenerado = null;
                    this.loadArsInfo(); // Refrescar estado para sincronizar if_pdf en DB
                } else {
                    this.metodosUi.showToast('Error al eliminar: ' + (res.message || ''), 'danger');
                }
            } catch (error) {
                console.error("Error deleting photographic report", error);
                this.metodosUi.showToast('Error de conexión al intentar eliminar', 'danger');
            } finally {
                this.isSaving = false;
            }
        }
    }

    async generarInformeFinal() {
        this.isSaving = true;
        try {
            const res: any = await firstValueFrom(
                this.javapiService.genericPost(`api/pg/trabajos/informe-final/generar/${this.trabajo.id}`, {})
            );

            if (res && res.status === 1) {
                this.metodosUi.showToast('Informe de Trabajo Final generado correctamente', 'success');
                await this.loadArsInfo(); // Refresh state
            } else {
                this.metodosUi.showToast(res.message || 'Error al generar el informe final', 'danger');
            }
        } catch (error: any) {
            console.error("Error generating final report", error);
            this.metodosUi.showToast(error?.error?.message || 'Error al generar el informe final', 'danger');
        } finally {
            this.isSaving = false;
        }
    }

    async presentInftActionSheet() {
        const actionSheet = await this.actionSheetCtrl.create({
            header: 'Informe de Trabajo Final',
            cssClass: 'custom-action-sheet',
            buttons: [
                {
                    text: 'Ver Informe Final',
                    icon: 'eye-outline',
                    handler: () => {
                        this.verInformeFinal();
                    }
                },
                {
                    text: 'Volver a Generar',
                    icon: 'refresh-outline',
                    handler: () => {
                        actionSheet.dismiss();
                        setTimeout(() => {
                            this.generarInformeFinal();
                        }, 300);
                        return false;
                    }
                },
                {
                    text: 'Eliminar Informe Final',
                    icon: 'trash-outline',
                    role: 'destructive',
                    handler: () => {
                        actionSheet.dismiss();
                        setTimeout(() => {
                            this.eliminarInformeFinal();
                        }, 300);
                        return false;
                    }
                },
                {
                    text: 'Cancelar',
                    icon: 'close-outline',
                    role: 'cancel'
                }
            ]
        });
        await actionSheet.present();
    }

    async verInformeFinal() {
        const url = this.javapiService.getFileUrl('ITF', String(this.trabajo.id), false);
        await Browser.open({ url });
    }

    async eliminarInformeFinal() {
        const resConfirm = await this.metodosUi.showConfirmation(
            'Confirmar Eliminación',
            '¿Estás seguro de que deseas eliminar este Informe de Trabajo Final? Se borrará del servidor.'
        );

        if (resConfirm) {
            this.isSaving = true;
            try {
                const res: any = await firstValueFrom(
                    this.javapiService.genericPost(`api/pg/trabajos/file/delete/INFT/${this.trabajo.id}`, {})
                );

                if (res && res.status === 1) {
                    this.metodosUi.showToast('Informe Final eliminado correctamente', 'success');
                    this.inftGenerado = false;
                    this.loadArsInfo(); // Refresh state
                } else {
                    this.metodosUi.showToast('Error al eliminar: ' + (res.message || ''), 'danger');
                }
            } catch (error) {
                console.error("Error deleting final report", error);
                this.metodosUi.showToast('Error de conexión al intentar eliminar', 'danger');
            } finally {
                this.isSaving = false;
            }
        }
    }
}
